// Car entities. Movement and spawn parameters verbatim from v1
// js/obstacle-manager.js:177-188/:2284; bodies are the v1 car types
// (createMovingCar :417-495): sedan/suv/compact with roofs, tires with rims
// and hub caps, emissive head/taillights and dark windows — plus the Phase 5
// slide-under box truck. Collision extents derive from each type's true size.

import * as THREE from 'three';
import {
  LANES,
  CAR_SPEED_BASE_FACTOR,
  CAR_SPEED_RANDOM_RANGE,
  CAR_SPAWN_Z_ONCOMING,
  CAR_SPAWN_Z_AWAY,
  TRUCK,
} from '@/config/tuning';
import { Pool } from '@/core/pool';
import type { Rng } from '@/core/rng';

export interface Car {
  group: THREE.Group;
  wheels: THREE.Object3D[];
  carSpeed: number;
  carDirection: 1 | -1;
  halfWidth: number;
  halfLength: number;
  topHeight: number;
  /** Box truck: trailer clears a sliding player; standing/jumping is fatal */
  underpass: boolean;
  nearMissTriggered: boolean;
  alive: boolean;
  typeId: string;
}

// v1 carTypes (js/obstacle-manager.js:423-428); v1's flatbed 'truck' is
// superseded by the Phase 5 box truck
interface CarTypeDef {
  id: string;
  width: number;
  height: number;
  length: number;
  wheelSize: number;
}

const CAR_TYPES: readonly CarTypeDef[] = [
  { id: 'sedan', width: 1.2, height: 0.9, length: 2.7, wheelSize: 0.28 },
  { id: 'suv', width: 1.4, height: 1.2, length: 3.3, wheelSize: 0.32 },
  { id: 'compact', width: 1.0, height: 0.8, length: 2.4, wheelSize: 0.25 },
];

// v1 car palette (js/obstacle-manager.js:420)
const CAR_COLORS = [
  0xff0000, 0x0066ff, 0x00aa00, 0xffff00, 0xff6600, 0x8800ff, 0x00ffff, 0xffffff, 0x444444,
  0x990000,
];

const tireMat = new THREE.MeshLambertMaterial({ color: 0x111111 });
const rimMat = new THREE.MeshPhongMaterial({ color: 0x888888, shininess: 50 });
const hubMat = new THREE.MeshPhongMaterial({ color: 0xaaaaaa, shininess: 60 });
const glassMat = new THREE.MeshLambertMaterial({ color: 0x223344 });
const headlightMat = new THREE.MeshLambertMaterial({ color: 0xfff8d0, emissive: 0xbba750 });
const taillightMat = new THREE.MeshLambertMaterial({ color: 0xdd2222, emissive: 0x881010 });
headlightMat.userData.noToon = true;
taillightMat.userData.noToon = true;

function buildWheel(radius: number): THREE.Group {
  const wheel = new THREE.Group();
  const tire = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, 0.18, 12), tireMat);
  tire.rotation.z = Math.PI / 2;
  wheel.add(tire);
  const rim = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 0.75, radius * 0.75, 0.2, 8),
    rimMat,
  );
  rim.rotation.z = Math.PI / 2;
  wheel.add(rim);
  const hub = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 0.4, radius * 0.4, 0.22, 6),
    hubMat,
  );
  hub.rotation.z = Math.PI / 2;
  wheel.add(hub);
  return wheel;
}

function buildCarMesh(type: CarTypeDef): { group: THREE.Group; wheels: THREE.Object3D[] } {
  const group = new THREE.Group();
  const clearance = 0.08;
  const bodyY = type.wheelSize + clearance + type.height / 2;

  const body = new THREE.Mesh(
    new THREE.BoxGeometry(type.width, type.height, type.length),
    new THREE.MeshLambertMaterial({ color: 0xff0000 }), // Recolored per spawn
  );
  body.position.y = bodyY;
  body.castShadow = true;
  group.add(body);

  // Cabin roof (v1 :450-468)
  const roof = new THREE.Mesh(
    new THREE.BoxGeometry(type.width * 0.75, type.height * 0.15, type.length * 0.55),
    new THREE.MeshLambertMaterial({ color: 0xff0000 }),
  );
  roof.position.set(0, bodyY + type.height / 2 + 0.08, 0);
  roof.castShadow = true;
  group.add(roof);

  // Windows: windshield + rear + side band, dark glass
  const windshield = new THREE.Mesh(
    new THREE.BoxGeometry(type.width * 0.7, type.height * 0.35, 0.03),
    glassMat,
  );
  windshield.position.set(0, bodyY + type.height * 0.42, type.length * 0.275);
  windshield.rotation.x = -0.35;
  group.add(windshield);
  const rearWindow = windshield.clone();
  rearWindow.position.z = -type.length * 0.275;
  rearWindow.rotation.x = 0.35;
  group.add(rearWindow);
  for (const side of [-1, 1]) {
    const sideWindow = new THREE.Mesh(
      new THREE.BoxGeometry(0.02, type.height * 0.3, type.length * 0.45),
      glassMat,
    );
    sideWindow.position.set(side * type.width * 0.38, bodyY + type.height * 0.42, 0);
    group.add(sideWindow);
  }

  // Head/taillights (nose = +z; spawn flips the group for the away lane)
  const lightGeo = new THREE.BoxGeometry(0.16, 0.08, 0.04);
  for (const side of [-1, 1]) {
    const headlight = new THREE.Mesh(lightGeo, headlightMat);
    headlight.position.set(
      side * type.width * 0.32,
      bodyY - type.height * 0.15,
      type.length / 2 + 0.02,
    );
    group.add(headlight);
    const taillight = new THREE.Mesh(lightGeo, taillightMat);
    taillight.position.set(
      side * type.width * 0.32,
      bodyY - type.height * 0.15,
      -type.length / 2 - 0.02,
    );
    group.add(taillight);
  }

  // Bumpers
  const bumperGeo = new THREE.BoxGeometry(type.width * 1.02, 0.1, 0.08);
  const bumperMat = new THREE.MeshLambertMaterial({ color: 0x333333 });
  for (const z of [type.length / 2 + 0.04, -type.length / 2 - 0.04]) {
    const bumper = new THREE.Mesh(bumperGeo, bumperMat);
    bumper.position.set(0, type.wheelSize + clearance + 0.08, z);
    group.add(bumper);
  }

  const wheels: THREE.Object3D[] = [];
  for (const [x, z] of [
    [-type.width / 2 - 0.08, type.length / 3],
    [type.width / 2 + 0.08, type.length / 3],
    [-type.width / 2 - 0.08, -type.length / 3],
    [type.width / 2 + 0.08, -type.length / 3],
  ] as const) {
    const wheel = buildWheel(type.wheelSize);
    wheel.position.set(x, type.wheelSize, z);
    group.add(wheel);
    wheels.push(wheel);
  }
  return { group, wheels };
}

/** Box truck: open underpass below the trailer — slide under it (Phase 5). */
function buildTruckMesh(): { group: THREE.Group; wheels: THREE.Object3D[] } {
  const group = new THREE.Group();
  const cab = new THREE.Mesh(
    new THREE.BoxGeometry(1.4, 1.3, 1.3),
    new THREE.MeshLambertMaterial({ color: 0x2c3e50 }),
  );
  cab.position.set(0, 0.85, 2.1);
  cab.castShadow = true;
  group.add(cab);

  const cabGlass = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.4, 0.04), glassMat);
  cabGlass.position.set(0, 1.15, 2.76);
  group.add(cabGlass);
  const lightGeo = new THREE.BoxGeometry(0.16, 0.08, 0.04);
  for (const side of [-1, 1]) {
    const headlight = new THREE.Mesh(lightGeo, headlightMat);
    headlight.position.set(side * 0.5, 0.5, 2.77);
    group.add(headlight);
  }

  const trailer = new THREE.Mesh(
    new THREE.BoxGeometry(1.5, TRUCK.TRAILER_TOP - TRUCK.TRAILER_CLEARANCE, 3.6),
    new THREE.MeshLambertMaterial({ color: 0xd9d9d9 }),
  );
  trailer.position.set(0, (TRUCK.TRAILER_TOP + TRUCK.TRAILER_CLEARANCE) / 2, -0.4);
  trailer.castShadow = true;
  group.add(trailer);

  const strutMat = new THREE.MeshLambertMaterial({ color: 0x555555 });
  for (const z of [-1.9, 1.1]) {
    for (const x of [-0.6, 0.6]) {
      const strut = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, TRUCK.TRAILER_CLEARANCE, 0.12),
        strutMat,
      );
      strut.position.set(x, TRUCK.TRAILER_CLEARANCE / 2, z);
      group.add(strut);
    }
  }

  const wheels: THREE.Object3D[] = [];
  for (const [x, z] of [
    [-0.7, 2.1],
    [0.7, 2.1],
    [-0.7, -1.6],
    [0.7, -1.6],
  ] as const) {
    const wheel = buildWheel(0.28);
    wheel.position.set(x, 0.28, z);
    group.add(wheel);
    wheels.push(wheel);
  }
  return { group, wheels };
}

function makeCar(type: CarTypeDef): Car {
  const { group, wheels } = buildCarMesh(type);
  return {
    group,
    wheels,
    carSpeed: 0,
    carDirection: 1,
    // Collision extents scale with the true body (sedan matches v1's base
    // 0.75/1.5 half-extents; topHeight = wheel + clearance + body height)
    halfWidth: type.width * 0.625,
    halfLength: type.length * 0.556,
    topHeight: type.wheelSize + 0.08 + type.height,
    underpass: false,
    nearMissTriggered: false,
    alive: false,
    typeId: type.id,
  };
}

export class CarSystem {
  readonly cars: Car[] = [];
  private readonly pools = new Map<string, Pool<Car>>();

  constructor(
    private readonly scene: THREE.Scene,
    private readonly rng: Rng,
  ) {
    for (const type of CAR_TYPES) {
      this.pools.set(
        type.id,
        new Pool<Car>(
          () => makeCar(type),
          (car) => {
            car.nearMissTriggered = false;
            car.alive = true;
          },
          2,
        ),
      );
    }
    this.pools.set(
      'boxtruck',
      new Pool<Car>(
        () => {
          const { group, wheels } = buildTruckMesh();
          return {
            group,
            wheels,
            carSpeed: 0,
            carDirection: 1,
            halfWidth: 0.75,
            halfLength: 2.7,
            topHeight: TRUCK.TRAILER_TOP,
            underpass: true,
            nearMissTriggered: false,
            alive: false,
            typeId: 'boxtruck',
          };
        },
        (car) => {
          car.nearMissTriggered = false;
          car.alive = true;
        },
        2,
      ),
    );
  }

  /** Spawn on a street lane (v1 js/obstacle-manager.js:159-189);
   *  a share of spawns are slide-under box trucks (Phase 5). */
  spawn(baseSpeed: number, carSpeedMultiplier: number): void {
    const isTruck = this.rng.next() < TRUCK.SPAWN_CHANCE;
    const typeId = isTruck
      ? 'boxtruck'
      : (CAR_TYPES[Math.floor(this.rng.next() * CAR_TYPES.length)] as CarTypeDef).id;
    const car = (this.pools.get(typeId) as Pool<Car>).acquire();
    if (!isTruck) {
      const body = car.group.children[0] as THREE.Mesh;
      const roof = car.group.children[1] as THREE.Mesh;
      const color = CAR_COLORS[Math.floor(this.rng.next() * CAR_COLORS.length)] ?? 0xff0000;
      (body.material as THREE.MeshLambertMaterial).color.setHex(color);
      // 30% of roofs are black (v1 :452)
      (roof.material as THREE.MeshLambertMaterial).color.setHex(
        this.rng.next() < 0.3 ? 0x222222 : color,
      );
    }

    const lane = this.rng.next() < 0.5 ? 1 : 2;
    const speed =
      baseSpeed *
      (CAR_SPEED_BASE_FACTOR + this.rng.next() * CAR_SPEED_RANDOM_RANGE) *
      carSpeedMultiplier;
    if (lane === 1) {
      // Oncoming from the horizon
      car.group.position.set(LANES[1], 0, CAR_SPAWN_Z_ONCOMING);
      car.carDirection = 1;
      car.group.rotation.y = 0;
    } else {
      // Same direction, moving away
      car.group.position.set(LANES[2], 0, CAR_SPAWN_Z_AWAY);
      car.carDirection = -1;
      car.group.rotation.y = Math.PI;
    }
    car.carSpeed = speed;
    this.scene.add(car.group);
    this.cars.push(car);
  }

  update(dt: number, gameSpeed: number): void {
    for (let i = this.cars.length - 1; i >= 0; i--) {
      const car = this.cars[i];
      if (!car) continue;
      // v1 js/obstacle-manager.js:2284
      car.group.position.z += (gameSpeed + car.carSpeed * car.carDirection) * dt * 60;
      for (const wheel of car.wheels) {
        wheel.rotation.x += car.carSpeed * car.carDirection * 3 * dt * 60;
      }
      const z = car.group.position.z;
      if (z > 25 || z < -90) this.despawn(i);
    }
  }

  despawn(index: number): void {
    const car = this.cars[index];
    if (!car) return;
    car.alive = false;
    this.scene.remove(car.group);
    this.cars.splice(index, 1);
    (this.pools.get(car.typeId) as Pool<Car>).release(car);
  }

  clear(): void {
    for (let i = this.cars.length - 1; i >= 0; i--) this.despawn(i);
  }
}
